import { romApiUrl, retroRomUrl } from './romApi.js';
import { externalEmulatorConfig, isExternalEmulatorKey } from './externalEmulators.js';
import { SYSTEMS } from './systems.js';

/**
 * Como cada tipo de ROM é carregada no portal.
 * @typedef {'emulatorjs' | 'azahar' | 'dolphin' | 'unsupported'} RomLoaderKind
 */

/** @type {Record<string, { kind: RomLoaderKind, ejsKey?: string, core?: string, label: string, exts: string[] }>} */
export const ROM_LOADER_BY_EXT = {
  nes: { kind: 'emulatorjs', ejsKey: 'nes', core: 'fceumm', label: 'NES', exts: ['nes', 'fds', 'unf', 'nsf'] },
  fds: { kind: 'emulatorjs', ejsKey: 'nes', core: 'fceumm', label: 'NES (FDS)', exts: ['fds'] },
  smc: { kind: 'emulatorjs', ejsKey: 'snes', core: 'snes9x', label: 'SNES', exts: ['smc', 'sfc', 'fig', 'swc'] },
  sfc: { kind: 'emulatorjs', ejsKey: 'snes', core: 'snes9x', label: 'SNES', exts: ['sfc'] },
  n64: { kind: 'emulatorjs', ejsKey: 'n64', core: 'mupen64plus_next', label: 'Nintendo 64', exts: ['n64', 'z64', 'v64'] },
  z64: { kind: 'emulatorjs', ejsKey: 'n64', core: 'mupen64plus_next', label: 'Nintendo 64', exts: ['z64'] },
  gba: { kind: 'emulatorjs', ejsKey: 'gba', core: 'mgba', label: 'Game Boy Advance', exts: ['gba'] },
  gb: { kind: 'emulatorjs', ejsKey: 'gb', core: 'gambatte', label: 'Game Boy', exts: ['gb'] },
  gbc: { kind: 'emulatorjs', ejsKey: 'gb', core: 'gambatte', label: 'Game Boy Color', exts: ['gbc'] },
  nds: { kind: 'emulatorjs', ejsKey: 'nds', core: 'desmume', label: 'Nintendo DS', exts: ['nds'] },
  gen: { kind: 'emulatorjs', ejsKey: 'segaMD', core: 'genesis_plus_gx', label: 'Mega Drive', exts: ['gen'] },
  md: { kind: 'emulatorjs', ejsKey: 'segaMD', core: 'genesis_plus_gx', label: 'Mega Drive', exts: ['md'] },
  smd: { kind: 'emulatorjs', ejsKey: 'segaMD', core: 'genesis_plus_gx', label: 'Mega Drive', exts: ['smd'] },
  sms: { kind: 'emulatorjs', ejsKey: 'segaMS', core: 'genesis_plus_gx', label: 'Master System', exts: ['sms'] },
  gg: { kind: 'emulatorjs', ejsKey: 'segaMS', core: 'genesis_plus_gx', label: 'Game Gear', exts: ['gg'] },
  a26: { kind: 'emulatorjs', ejsKey: 'atari2600', core: 'stella2014', label: 'Atari 2600', exts: ['a26'] },
  pbp: { kind: 'emulatorjs', ejsKey: 'psx', core: 'pcsx_rearmed', label: 'PlayStation', exts: ['pbp'] },
  chd: { kind: 'emulatorjs', ejsKey: 'psx', core: 'pcsx_rearmed', label: 'PlayStation', exts: ['chd'] },
  cue: { kind: 'emulatorjs', ejsKey: 'psx', core: 'pcsx_rearmed', label: 'PlayStation', exts: ['cue'] },
  iso: { kind: 'emulatorjs', ejsKey: 'psx', core: 'pcsx_rearmed', label: 'PlayStation / multi', exts: ['iso'] },
  '3ds': { kind: 'azahar', ejsKey: '3ds', label: 'Nintendo 3DS', exts: ['3ds'] },
  cci: { kind: 'azahar', ejsKey: '3ds', label: 'Nintendo 3DS', exts: ['cci'] },
  cia: { kind: 'azahar', ejsKey: '3ds', label: 'Nintendo 3DS', exts: ['cia'] },
  wbfs: { kind: 'dolphin', ejsKey: 'wii', label: 'Nintendo Wii', exts: ['wbfs'] },
  wad: { kind: 'dolphin', ejsKey: 'wii', label: 'Nintendo Wii', exts: ['wad'] },
  gcm: { kind: 'dolphin', ejsKey: 'wii', label: 'GameCube', exts: ['gcm', 'gc'] },
  gc: { kind: 'dolphin', ejsKey: 'wii', label: 'GameCube', exts: ['gc'] },
  rvz: { kind: 'dolphin', ejsKey: 'wii', label: 'Wii/GameCube', exts: ['rvz'] },
  ciso: { kind: 'dolphin', ejsKey: 'wii', label: 'Wii/GameCube', exts: ['ciso'] },
};

/** Lista agrupada para UI (consola → motor → extensões). */
export function romLoaderCatalog() {
  const groups = new Map();

  for (const row of Object.values(ROM_LOADER_BY_EXT)) {
    const key = row.ejsKey || row.kind;
    if (!groups.has(key)) {
      groups.set(key, {
        ejsKey: key,
        kind: row.kind,
        label: row.label,
        core: row.core || null,
        exts: new Set(),
        engine:
          row.kind === 'azahar'
            ? 'Azahar (Selkies)'
            : row.kind === 'dolphin'
              ? 'Dolphin (Selkies)'
              : `EmulatorJS (${row.core || key})`,
        romSource: row.kind === 'emulatorjs' ? 'Retro /play/retro/roms/' : 'Servidor /roms + fila',
      });
    }
    row.exts.forEach((e) => groups.get(key).exts.add(e));
  }

  return [...groups.values()]
    .map((g) => ({ ...g, exts: [...g.exts].sort() }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

export function loaderForFilename(filename) {
  const ext = String(filename || '').split('.').pop()?.toLowerCase() || '';
  return ROM_LOADER_BY_EXT[ext] || null;
}

export function describeGameLoader(game) {
  const filename = game?.filename || game?.title || '';
  const ext = filename.includes('.') ? filename.split('.').pop().toLowerCase() : '';
  const byExt = ROM_LOADER_BY_EXT[ext];
  const ejsKey = game?.ejsKey || byExt?.ejsKey;
  const kind = isExternalEmulatorKey(ejsKey)
    ? ejsKey === '3ds'
      ? 'azahar'
      : 'dolphin'
    : 'emulatorjs';

  if (kind === 'azahar') {
    return {
      kind,
      engine: 'Azahar (Selkies)',
      core: null,
      romUrl: game?.filename ? romApiUrl(game.filename) : null,
      playUrl: externalEmulatorConfig('3ds')?.playPath || '/play/3ds/',
      note: 'ROM no servidor → fila → Azahar abre /roms/<ficheiro>',
    };
  }

  if (kind === 'dolphin') {
    return {
      kind,
      engine: 'Dolphin (Selkies)',
      core: null,
      romUrl: game?.filename ? romApiUrl(game.filename) : null,
      playUrl: externalEmulatorConfig('wii')?.playPath || '/play/wii/',
      note: 'ROM no servidor → fila → Dolphin abre /roms/<ficheiro>',
    };
  }

  const core = SYSTEMS[ejsKey]?.ejsCore || ejsKey || byExt?.core || 'auto';
  const retroUrl = filename ? retroRomUrl(filename) : game?.url || null;

  return {
    kind: 'emulatorjs',
    engine: 'EmulatorJS (browser)',
    core,
    romUrl: retroUrl,
    playUrl: null,
    note: `Core ${core} — ROM via ${retroUrl ? '/play/retro/roms/' : 'upload local'}`,
  };
}

/**
 * ROM para EmulatorJS: URL same-origin (EJS faz fetch interno).
 * Fallback: blob via /api/roms.
 */
export async function loadRomForEmulatorJs(filename, urlOrFile) {
  if (urlOrFile instanceof File) return urlOrFile;

  const name = filename || String(urlOrFile || '').split('/').pop()?.split('?')[0];
  const staticUrl = name ? retroRomUrl(name) : null;

  if (staticUrl) {
    try {
      const head = await fetch(staticUrl, { method: 'HEAD', credentials: 'include', cache: 'no-store' });
      const resolved = head.headers.get('X-Rom-Resolved');
      // Vault pede .zip mas o disco tem .nes/.nds/… — usa File com o nome real
      if (head.ok && resolved && resolved !== name) {
        const { fetchRomForEmulator } = await import('./romApi.js');
        return fetchRomForEmulator(urlOrFile || staticUrl, name);
      }
      if (head.ok) return staticUrl;
    } catch {
      /* tenta GET abaixo */
    }
    try {
      const res = await fetch(staticUrl, { credentials: 'include', cache: 'no-store' });
      const resolved = res.headers.get('X-Rom-Resolved');
      if (res.ok && resolved && resolved !== name) {
        const { fetchRomForEmulator } = await import('./romApi.js');
        return fetchRomForEmulator(urlOrFile || staticUrl, name);
      }
      if (res.ok) return staticUrl;
    } catch {
      /* fallback api */
    }
  }

  const { fetchRomForEmulator } = await import('./romApi.js');
  return fetchRomForEmulator(urlOrFile || staticUrl, name);
}
